import React from 'react';
import { Box, Button, Spacer, Text } from '@zillow/constellation-10';
import { Container } from '@zg-rentals/zrs-comet';
import { css } from '@/styled-system/css';

import {
  CometUserRoleType,
  emailUtils,
  HeadWrapper,
  LoadingStates,
  RentalManagerUserManagementCompanyWebApiHandler_getUserCompanyDetails,
  RentalManagerUserManagementOnboardingWebApiHandler_onboardPendingUser,
  SpinnerContainer,
  useRentalManagerUser,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';
import type { UserManagementCompanyDetailsDto } from '@zg-rentals/rental-manager-env';
import type { UserManagementInvitationDto } from '@zg-rentals/ts-user-management-client';
import {
  UmcCompanyOverviewWebApiHandler_declineUserManagementInvitation,
  UmcCompanyOverviewWebApiHandler_getUserManagementInvitation,
} from '@zg-rentals/ts-user-management-client';

const DEFAULT_PAGE_TITLE = 'Welcome to Rental Manager';
const { LOADING, ERROR, INITIAL, SUCCESS } = LoadingStates;

const styledContainerStyles = css({
  backgroundColor: 'bg.neutral.base',
  borderRadius: '5px',
  border: '1px solid',
  borderColor: 'border.neutral.secondary',
  marginBottom: '1200',
  marginTop: '400',
  padding: '400',
});

const UserManagementInvitationPage = () => {
  const paths = useRmwPaths();
  const { user } = useRentalManagerUser();
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [loadingState, setLoadingState] = React.useState<LoadingStates>(INITIAL);
  const [companyInvite, setCompanyInvite] = React.useState<UserManagementInvitationDto>();
  const [companyDetails, setCompanyDetails] = React.useState<UserManagementCompanyDetailsDto>();

  React.useEffect(() => {
    if (
      user.roleTypes?.includes(CometUserRoleType.PROPERTY_MANAGER_COMPANY_ADMIN) ||
      user.roleTypes?.includes(CometUserRoleType.PROPERTY_MANAGER_COMPANY_USER)
    ) {
      RentalManagerUserManagementCompanyWebApiHandler_getUserCompanyDetails()
        .then((companyDetails) => {
          setCompanyDetails(companyDetails);
          setLoadingState(SUCCESS);
          return;
        })
        .catch(() => {
          setErrorMessage('Failed to get company information.');
          setLoadingState(ERROR);
        });
      return;
    }
    setLoadingState(LOADING);
    UmcCompanyOverviewWebApiHandler_getUserManagementInvitation()
      .then((data) => {
        setCompanyInvite(data);
        setLoadingState(SUCCESS);
        return;
      })
      .catch(() => {
        setErrorMessage('Failed to get invitation');
        setLoadingState(ERROR);
      });
  }, []);

  const acceptInvitation = () => {
    setLoadingState(LOADING);
    RentalManagerUserManagementOnboardingWebApiHandler_onboardPendingUser()
      .then(() => {
        document.location.href = paths.propertiesPage;
        return;
      })
      .catch(() => {
        setLoadingState(ERROR);
      });
  };

  const declineInvitation = () => {
    setLoadingState(LOADING);
    UmcCompanyOverviewWebApiHandler_declineUserManagementInvitation()
      .then(() => {
        document.location.href = paths.propertiesPage;
        return;
      })
      .catch(() => {
        setErrorMessage('Failed to decline invitation');
        setLoadingState(ERROR);
      });
  };

  const renderInvitation = () => {
    if (!companyInvite) return null;
    return (
      <>
        <Text asChild>
          <div>
            You&apos;re invited to join&nbsp;
            <strong>{companyInvite.companyName}</strong>. By accepting, all the properties in your account will be added
            to the company and you will be granted access to company properties.
          </div>
        </Text>
        <Spacer css={{ marginBlock: 'sm' }}>
          <Text asChild>
            <div>
              If you don&apos;t want your properties to be added to the company, please decline the invite, contact
              support at
              {` ${emailUtils.getHelpEmail(user.isMultifamilyCustomer, user.isSingleFamilyEnterpriseCustomer)} `}
              to remove unwanted properties from your account and then request your company admin to invite you again
              later.
            </div>
          </Text>
        </Spacer>
        <Spacer css={{ marginBlockEnd: 'xs' }}>
          <Button emphasis="outlined" onClick={acceptInvitation}>
            Accept Invitation
          </Button>
        </Spacer>
        <Button emphasis="outlined" onClick={declineInvitation}>
          Decline Invitation
        </Button>
      </>
    );
  };

  const renderInfo = () => {
    if (companyInvite?.isInvitationFound) return renderInvitation();
    if (
      user.roleTypes?.includes(CometUserRoleType.PROPERTY_MANAGER_COMPANY_USER) ||
      user.roleTypes?.includes(CometUserRoleType.PROPERTY_MANAGER_COMPANY_ADMIN)
    ) {
      return (
        <>
          <Text textStyle="body" asChild>
            <p>Invitation for {companyDetails?.companyName} has been accepted!</p>
          </Text>
          <Button
            onClick={() => {
              document.location.href = paths.propertiesPage;
            }}
            emphasis="outlined"
          >
            Continue to manage properties
          </Button>
        </>
      );
    }

    return (
      <>
        <Text textStyle="body" asChild>
          <p>No invitation found at this time.</p>
        </Text>
        <Text asChild>
          <p>You are currently logged in as {user.email}.</p>
        </Text>
        <Text textStyle="body-sm-bold" asChild>
          <p>
            If you have an invitation under a different account, please log out first, then log in as the invited
            account.
          </p>
        </Text>
      </>
    );
  };

  return (
    <div>
      <Container>
        <Box className={styledContainerStyles}>
          <HeadWrapper title={DEFAULT_PAGE_TITLE} />
          <Spacer css={{ marginBlock: 'sm' }}>
            <Text textStyle="heading-md">{DEFAULT_PAGE_TITLE}</Text>
          </Spacer>
          {loadingState === SUCCESS && renderInfo()}
          {(loadingState === LOADING || loadingState === INITIAL) && <SpinnerContainer />}
          {loadingState === ERROR &&
            (errorMessage ? (
              <Text asChild>
                <div>{errorMessage}</div>
              </Text>
            ) : (
              <>
                <Text asChild>
                  <div>Failed to load your settings.</div>
                </Text>
                <Text asChild>
                  <div>Please refresh, or check with your company if you have an invitation.</div>
                </Text>
              </>
            ))}
        </Box>
      </Container>
    </div>
  );
};

export default UserManagementInvitationPage;
